import type { FC, ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { SignalSourceTags } from '@/features/signals/components/SignalSourceLine';
import type { SignalSource, SignalSourcesList } from '@/features/signals/type/SignalsTypes';

// Radix Select refuses '' as an item value.
const NONE = '__none__';

interface SignalSourceSelectProps {
  /** The machine's signal source (its kafkaName), null when none is linked. */
  value: string | null;
  onChange: (source: string | null) => void;
  sources: SignalSourcesList | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Source name → name of the other machine of the company already linked to it. */
  linkedBy: Map<string, string>;
  disabled: boolean;
}

function notFound(name: string): SignalSource {
  return { name, influx: false, liveData: false, lastTime: null };
}

/**
 * Where a machine's signals are recorded, picked from the measurements of the signals database:
 * "Active now" (a point in the last hour) and "History only".
 */
export const SignalSourceSelect: FC<SignalSourceSelectProps> = ({
  value,
  onChange,
  sources,
  isLoading,
  error,
  onRetry,
  linkedBy,
  disabled,
}) => {
  const { t } = useTranslation();
  const list = sources?.sources ?? [];
  const active = list.filter((source) => source.liveData === true);
  const historyOnly = list.filter((source) => source.liveData !== true);
  // The worker answers an empty list when it cannot read the database: same as a load error.
  const unreadable = error !== null || sources?.influxAvailable === false;
  // A saved name the database no longer lists stays visible (and selectable), tagged "not in the database".
  const current = value ? (list.find((source) => source.name === value) ?? notFound(value)) : null;
  const currentIsListed = current !== null && list.some((source) => source.name === current.name);

  const item = (source: SignalSource) => {
    const takenBy = linkedBy.get(source.name);
    return (
      <SelectItem key={source.name} value={source.name} disabled={takenBy !== undefined}>
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[13px]">{source.name}</span>
          {!unreadable && <SignalSourceTags source={source} linkedTo={takenBy} showTime />}
        </span>
      </SelectItem>
    );
  };

  let helper: ReactNode;
  if (unreadable) {
    helper = (
      <Callout size="inline" tone="error" data-testid="signal-sources-error">
        {t('traceability.machineManager.form.fields.kafkaName.loadError')}{' '}
        <Button type="button" variant="link" size="inline" onClick={onRetry}>
          {t('traceability.machineManager.form.fields.kafkaName.retry')}
        </Button>
      </Callout>
    );
  } else if (current && current.influx && current.liveData === false) {
    helper = warning(t('traceability.machineManager.form.fields.kafkaName.historyOnlyWarning'));
  } else {
    helper = (
      <p className="text-xs text-muted-foreground">
        {t('traceability.machineManager.form.fields.kafkaName.help')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground" htmlFor="machine-signal-source">
        {t('traceability.machineManager.form.fields.kafkaName.label')}
      </label>
      <Select
        value={value ?? NONE}
        onValueChange={(next) => onChange(next === NONE ? null : next)}
        disabled={disabled || isLoading}
      >
        <SelectTrigger
          id="machine-signal-source"
          className="w-full"
          data-testid="machine-signal-source-trigger"
        >
          <SelectValue
            placeholder={t('traceability.machineManager.form.fields.kafkaName.placeholder')}
          >
            {current ? (
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate font-mono text-[13px]">{current.name}</span>
                {!unreadable && <SignalSourceTags source={current} showTime />}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t('traceability.machineManager.form.fields.kafkaName.placeholder')}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            {t('traceability.machineManager.form.fields.kafkaName.placeholder')}
          </SelectItem>
          {current && !currentIsListed && item(current)}
          {active.length > 0 && (
            <SelectGroup>
              <SelectLabel>
                {t('traceability.machineManager.form.fields.kafkaName.groupActive')}
              </SelectLabel>
              {active.map(item)}
            </SelectGroup>
          )}
          {historyOnly.length > 0 && (
            <SelectGroup>
              <SelectLabel>
                {t('traceability.machineManager.form.fields.kafkaName.groupHistory')}
              </SelectLabel>
              {historyOnly.map(item)}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
      {helper}
    </div>
  );
};

function warning(message: string) {
  return (
    <Callout size="inline" tone="warning" data-testid="signal-source-warning">
      {message}
    </Callout>
  );
}
